// Writes. Privileged operations go through callable Functions whose request
// contracts come from @taller/shared (validated here too, so the user gets
// the error before the round trip). The few direct writes are exactly the
// ones firestore.rules allow for the client.
import {
  MENSAJE_TALLER_SUSPENDIDO,
  MOTIVO_TALLER_SUSPENDIDO,
  usoDe,
  type Callables,
  type RespuestaAceptarTerminos,
  type RespuestaEmitirSeguimiento,
  type RespuestaRevocarSeguimiento,
  type RespuestaEnviarCorreoTipo,
  type RespuestaGuardarPlantillaCorreo,
  type RespuestaConvertirContacto,
  type RespuestaInvitarAPrueba,
  type RespuestaExportarDatos,
  type RespuestaGuardarFicha,
  type ResultadoBienvenida,
  type ResumenBajaTaller,
  type SolicitudSeguimiento,
} from '@taller/shared';
import { FirebaseError } from 'firebase/app';
import {
  addDoc,
  collection,
  deleteField,
  doc,
  runTransaction,
  serverTimestamp,
  Timestamp,
  updateDoc,
  type FieldValue,
} from 'firebase/firestore';
import { httpsCallable, type HttpsCallableOptions } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import type { EstadoEditable } from '@/plataforma/contactos';
import {
  CuponExistente,
  type CambiosCupon,
  type CuponDoc,
  type DatosNuevoCupon,
} from '@/plataforma/cupones';
import type { Canal, OrdenDoc } from './tipos';

type Nombre = keyof Callables;

export function llamar<K extends Nombre, R = unknown>(nombre: K, opciones?: HttpsCallableOptions) {
  const fn = httpsCallable<Callables[K], R>(functions, nombre, opciones);
  return async (datos: Callables[K]): Promise<R> => (await fn(datos)).data;
}

/** New account + its temporary password (shown once, never stored). */
export type ConClave = { uid: string; password: string };

export const callables = {
  moverOrden: llamar<'moverOrden'>('moverOrden'),
  crearUsuario: llamar<'crearUsuario', ConClave & { bienvenida?: ResultadoBienvenida }>(
    'crearUsuario',
  ),
  reenviarBienvenida: llamar<'reenviarBienvenida', { ok: true }>('reenviarBienvenida'),
  sincronizarClave: llamar<'sincronizarClave', { debeCambiarPassword: boolean }>(
    'sincronizarClave',
  ),
  actualizarUsuario: llamar<'actualizarUsuario'>('actualizarUsuario'),
  fijarPin: llamar<'fijarPin'>('fijarPin'),
  quitarPin: llamar<'quitarPin'>('quitarPin'),
  restablecerPassword: llamar<'restablecerPassword', { password: string }>('restablecerPassword'),
  cambiarPassword: llamar<'cambiarPassword'>('cambiarPassword'),
  // Public callable: with App Check enforced its token is consumed on the
  // server (replay protection), so every call needs a fresh limited-use one.
  solicitarRestablecerClave: llamar<'solicitarRestablecerClave', { ok: true }>(
    'solicitarRestablecerClave',
    { limitedUseAppCheckTokens: true },
  ),
  crearEquipo: llamar<'crearEquipo', ConClave>('crearEquipo'),
  actualizarEquipo: llamar<'actualizarEquipo'>('actualizarEquipo'),
  aprobarDispositivo: llamar<'aprobarDispositivo'>('aprobarDispositivo'),
  revocarDispositivo: llamar<'revocarDispositivo'>('revocarDispositivo'),
  actualizarConfig: llamar<'actualizarConfig'>('actualizarConfig'),
  crearTaller: llamar<'crearTaller', { tallerId: string }>('crearTaller'),
  convertirContacto: llamar<'convertirContacto', RespuestaConvertirContacto>('convertirContacto'),
  // Creating the workshop waits for its example car (server triggers): as
  // long as the function may run (9 min) instead of the SDK's default 70 s.
  invitarAPrueba: llamar<'invitarAPrueba', RespuestaInvitarAPrueba>('invitarAPrueba', {
    timeout: 10 * 60_000,
  }),
  guardarFichaComercial: llamar<'guardarFichaComercial', RespuestaGuardarFicha>(
    'guardarFichaComercial',
  ),
  resumenBajaTaller: llamar<'resumenBajaTaller', ResumenBajaTaller>('resumenBajaTaller'),
  aceptarTerminos: llamar<'aceptarTerminos', RespuestaAceptarTerminos>('aceptarTerminos'),
  // A big workshop's ZIP takes minutes: wait as long as the function may run
  // (30 min) instead of the SDK's default 70 s.
  exportarDatosTaller: llamar<'exportarDatosTaller', RespuestaExportarDatos>(
    'exportarDatosTaller',
    { timeout: 31 * 60_000 },
  ),
  guardarPlantillaCorreo: llamar<'guardarPlantillaCorreo', RespuestaGuardarPlantillaCorreo>(
    'guardarPlantillaCorreo',
  ),
  guardarEnlacesCorreo: llamar<'guardarEnlacesCorreo', Record<string, never>>(
    'guardarEnlacesCorreo',
  ),
  enviarCorreoTipo: llamar<'enviarCorreoTipo', RespuestaEnviarCorreoTipo>('enviarCorreoTipo'),
  interruptorEnlacesPdf: llamar<'interruptorEnlacesPdf', { activo: boolean; cambiado: boolean }>(
    'interruptorEnlacesPdf',
  ),
};

const ruta = (t: string, ...p: string[]) => ['talleres', t, ...p].join('/');

// ---- Direct writes allowed by the rules ----

export function guardarRepuestos(t: string, ordenId: string, repuestos: OrdenDoc['repuestos']) {
  // Firestore rejects `undefined` inside arrays: strip empty optionals.
  const limpios = repuestos.map((r) =>
    Object.fromEntries(Object.entries(r).filter(([, v]) => v !== undefined && v !== '')),
  );
  return updateDoc(doc(db, ruta(t, 'ordenes', ordenId)), { repuestos: limpios });
}

export function guardarRetencion(t: string, ordenId: string, retencion: OrdenDoc['retencion']) {
  return updateDoc(doc(db, ruta(t, 'ordenes', ordenId)), { retencion });
}

export function revisarInspeccion(
  t: string,
  id: string,
  uid: string,
  porNombre: string,
  resultado: 'aprobada' | 'observada',
  nota: string,
) {
  const revision: Record<string, string | FieldValue> = {
    por: uid,
    porNombre,
    resultado,
    en: serverTimestamp(),
  };
  if (nota.trim()) revision['nota'] = nota.trim();
  return updateDoc(doc(db, ruta(t, 'inspecciones', id)), { revision });
}

export function anularInspeccion(
  t: string,
  id: string,
  uid: string,
  porNombre: string,
  motivo: string,
) {
  return updateDoc(doc(db, ruta(t, 'inspecciones', id)), {
    estado: 'anulada',
    anulacion: { por: uid, porNombre, motivo: motivo.trim(), en: serverTimestamp() },
  });
}

export function registrarAviso(
  t: string,
  aviso: {
    ordenId?: string;
    inspeccionId?: string;
    patente: string;
    canal: Canal;
    plantilla: string;
    mensaje: string;
    destino: string;
    por: string;
    porNombre: string;
  },
) {
  return addDoc(collection(db, ruta(t, 'avisos')), { ...aviso, en: serverTimestamp() });
}

// Customer tracking page (roadmap Q4 F1, shared/src/seguimiento.ts). Not in
// shared's callables map on purpose: importing that map would carry every
// callable name into the bundle (lecciones-agentes).
const fnEmitirSeguimiento = httpsCallable<SolicitudSeguimiento, RespuestaEmitirSeguimiento>(
  functions,
  'emitirSeguimiento',
);
const fnRevocarSeguimiento = httpsCallable<SolicitudSeguimiento, RespuestaRevocarSeguimiento>(
  functions,
  'revocarSeguimiento',
);

/** The order's current tracking link (reused while valid) or a new one. */
export async function emitirSeguimiento(ordenId: string) {
  return (await fnEmitirSeguimiento({ ordenId })).data;
}

export async function revocarSeguimiento(ordenId: string) {
  return (await fnRevocarSeguimiento({ ordenId })).data;
}

/**
 * Lead follow-up (superadmin): the rules only let `estado` and, while lost,
 * `motivoPerdida` change. Leaving 'perdido' drops its reason in the same
 * write (the rules reject a reason on any other status).
 */
export function cambiarEstadoContacto(id: string, estado: EstadoEditable) {
  return updateDoc(
    doc(db, 'contactos', id),
    estado === 'perdido' ? { estado } : { estado, motivoPerdida: deleteField() },
  );
}

/** Optional reason of a lost lead; empty removes it. */
export function guardarMotivoPerdida(id: string, motivo: string) {
  const texto = motivo.trim();
  return updateDoc(doc(db, 'contactos', id), { motivoPerdida: texto || deleteField() });
}

/**
 * Lead → client (superadmin). Only this callable sets `estado: 'cliente'`:
 * in one transaction it applies the lead's coupon (one more `usos`) or, with
 * `sinCupon`, records that it was not applied. `failed-precondition` means
 * the coupon is exhausted or expired.
 */
/** "Invitar a la prueba": the lead's real workshop and Correo A (shared/src/invitacion.ts). */
export function invitarAPrueba(datos: Callables['invitarAPrueba']) {
  return callables.invitarAPrueba(datos);
}

// ---- Correos tipo (Plataforma → Correos; superadmin, callables) ----

export function guardarPlantillaCorreo(datos: Callables['guardarPlantillaCorreo']) {
  return callables.guardarPlantillaCorreo(datos);
}

export function guardarEnlacesCorreo(datos: Callables['guardarEnlacesCorreo']) {
  return callables.guardarEnlacesCorreo(datos);
}

export function enviarCorreoTipo(datos: Callables['enviarCorreoTipo']) {
  return callables.enviarCorreoTipo(datos);
}

export function convertirContacto(contactoId: string, sinCupon = false) {
  return callables.convertirContacto(sinCupon ? { contactoId, sinCupon } : { contactoId });
}

// ---- Coupons (superadmin; firestore.rules fix codigo, usos, creadoEn, creadoPor) ----

const instante = (ms: number) => Timestamp.fromMillis(ms);

/**
 * Creates `cupones/{CODIGO}` only if the id is free. A plain `set` on an
 * existing code would be an update the rules reject with a bare
 * permission error; the transaction reads first and says it clearly.
 */
export async function crearCupon(c: DatosNuevoCupon, uid: string): Promise<void> {
  const ref = doc(db, 'cupones', c.codigo);
  await runTransaction(db, async (tx) => {
    if ((await tx.get(ref)).exists()) throw new CuponExistente(c.codigo);
    tx.set(ref, {
      codigo: c.codigo,
      descripcion: c.descripcion,
      uso: c.uso,
      tipo: c.tipo,
      valor: c.valor,
      duracionMeses: c.duracionMeses,
      activo: true,
      ...(c.vigenteDesde !== null && { vigenteDesde: instante(c.vigenteDesde) }),
      ...(c.vigenteHasta !== null && { vigenteHasta: instante(c.vigenteHasta) }),
      ...(c.usosMax !== null && { usosMax: c.usosMax }),
      usos: 0,
      creadoEn: serverTimestamp(),
      creadoPor: uid,
    });
  });
}

/** An emptied limit is removed from the doc (absent = no limit). */
const limite = <T>(v: T | null, a: (v: T) => unknown) => (v === null ? deleteField() : a(v));

/** Audit stamps firestore.rules accept on a coupon update (must be the truth). */
const sello = (uid: string) => ({ actualizadoEn: serverTimestamp(), actualizadoPor: uid });

export function editarCupon(codigo: string, cambios: CambiosCupon, uid: string) {
  return updateDoc(doc(db, 'cupones', codigo), {
    ...sello(uid),
    descripcion: cambios.descripcion,
    uso: cambios.uso,
    vigenteDesde: limite(cambios.vigenteDesde, instante),
    vigenteHasta: limite(cambios.vigenteHasta, instante),
    usosMax: limite(cambios.usosMax, (n) => n),
  });
}

/** Also writes `uso`, so a coupon stored before it passes the rules. */
export function fijarActivoCupon(c: CuponDoc, activo: boolean, uid: string) {
  return updateDoc(doc(db, 'cupones', c.codigo), { activo, uso: usoDe(c), ...sello(uid) });
}

// ---- Errors in plain Spanish ----

const porCodigo: Record<string, string> = {
  'functions/permission-denied': 'No tienes permiso para hacer esto.',
  'permission-denied': 'No tienes permiso para hacer esto.',
  'functions/unauthenticated': 'Tu sesión expiró. Vuelve a ingresar.',
  'functions/unavailable':
    'No hay conexión con el servidor. Revisa tu internet e inténtalo de nuevo.',
  unavailable: 'No hay conexión con el servidor. Revisa tu internet e inténtalo de nuevo.',
  'functions/deadline-exceeded': 'El servidor tardó demasiado en responder. Inténtalo de nuevo.',
  'functions/not-found': 'La operación no está disponible todavía en el servidor.',
  'functions/internal': 'Ocurrió un error en el servidor. Inténtalo de nuevo en un momento.',
  'functions/aborted':
    'Otra persona cambió lo mismo al mismo tiempo. Revisa y vuelve a intentarlo.',
  aborted: 'Otra persona cambió lo mismo al mismo tiempo. Revisa y vuelve a intentarlo.',
  'functions/already-exists': 'Ya existe.',
  'auth/weak-password': 'La contraseña es muy débil: usa al menos 8 caracteres.',
  'auth/requires-recent-login': 'Por seguridad, vuelve a ingresar y repite el cambio.',
  'auth/invalid-credential': 'Correo o contraseña incorrectos.',
  'auth/wrong-password': 'Correo o contraseña incorrectos.',
  'auth/user-not-found': 'Correo o contraseña incorrectos.',
  // Also every account of a workshop dado de baja (guardarFichaComercial):
  // the sign-in cannot tell which, and must not reveal it to anyone typing an e-mail.
  'auth/user-disabled':
    'Esta cuenta está desactivada. Si tu taller dejó de usar Tallerza, escríbenos a contacto@tallerza.cl para reactivarlo; si no, habla con el administrador del taller.',
  'auth/too-many-requests': 'Demasiados intentos. Espera unos minutos o restablece tu contraseña.',
  'auth/network-request-failed': 'No hay conexión. Revisa tu internet.',
  'functions/resource-exhausted': 'Demasiados intentos. Espera un rato e inténtalo de nuevo.',
};

/**
 * User-facing message. Functions errors (HttpsError) carry their own Spanish
 * message for business rules (e.g. "Solo se puede volver a una fase anterior")
 * which is more precise than any generic mapping, so it wins when present.
 */
/**
 * Set by the workshop shell while its workshop is suspended (read-only,
 * `talleres/{t}.soloLectura`): a write the rules deny is then explained as
 * the suspension, not as a missing permission.
 */
let tallerSoloLectura = false;
export function fijarSoloLectura(v: boolean) {
  tallerSoloLectura = v;
}
const NEGADO_POR_REGLAS = new Set(['permission-denied', 'storage/unauthorized']);

export function mensajeError(e: unknown): string {
  if (e instanceof FirebaseError) {
    const motivo = (e as FirebaseError & { details?: { motivo?: unknown } }).details?.motivo;
    if (motivo === MOTIVO_TALLER_SUSPENDIDO) return MENSAJE_TALLER_SUSPENDIDO;
    if (tallerSoloLectura && NEGADO_POR_REGLAS.has(e.code)) return MENSAJE_TALLER_SUSPENDIDO;
    // The Functions SDK appends the HTTP status ("... [409]"): never shown.
    const mensaje = e.message.replace(/\s*\[\d{3}\]$/, '');
    // HttpsError thrown on purpose by our Functions: its message is already
    // Spanish and specific. Default SDK messages are the bare code
    // ("internal", "NOT_FOUND"...) or English, and never help the user.
    const propio =
      e.code.startsWith('functions/') &&
      e.code !== 'functions/internal' &&
      mensaje &&
      !/^[A-Z_]+$|^[a-z-]+$/.test(mensaje) &&
      /[áéíóúñ¿¡]|\b(el|la|los|las|no|de|ya|se)\b/i.test(mensaje);
    if (propio) return mensaje;
    return porCodigo[e.code] ?? 'Algo salió mal. Inténtalo de nuevo.';
  }
  if (e instanceof Error && e.message) return e.message;
  return 'Algo salió mal. Inténtalo de nuevo.';
}
